import type { ReactNode, SyntheticEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, BottomNavigation, BottomNavigationAction } from '@mui/material';
import {
  CurrencyExchange as PairsIcon,
  Calculate as CalculatorIcon,
  Person as ProfileIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';

interface LayoutProps {
  children: ReactNode;
  showAdminTab?: boolean;
}

export default function Layout({ children, showAdminTab = true }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { t } = useI18n();

  const getNavValue = () => {
    if (location.pathname.startsWith('/admin')) return 'admin';
    switch (location.pathname) {
      case '/':
        return 'pairs';
      case '/calculator':
        return 'calculator';
      case '/profile':
        return 'profile';
      default:
        return 'pairs';
    }
  };

  const handleNavChange = (_: SyntheticEvent, newValue: string) => {
    switch (newValue) {
      case 'pairs':
        navigate('/');
        break;
      case 'calculator':
        navigate('/calculator');
        break;
      case 'profile':
        navigate('/profile');
        break;
      case 'admin':
        navigate('/admin');
        break;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'var(--app-viewport-stable-height, 100dvh)',
        minHeight: 'var(--app-viewport-stable-height, 100dvh)',
        overflow: 'hidden',
      }}
    >
      <Box
        component="main"
        sx={{
          flex: 1,
          overflow: 'auto',
          width: '100%',
          maxWidth: 'var(--app-content-max-width)',
          mx: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          pl: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-left))',
          pr: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-right))',
          pb: 'calc(var(--app-safe-area-bottom) + var(--app-bottom-nav-gap))',
          pointerEvents: 'none',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 'var(--app-bottom-obstruction)',
            overflow: 'hidden',
            pointerEvents: 'none',
            backdropFilter: 'blur(28px) saturate(155%)',
            WebkitBackdropFilter: 'blur(28px) saturate(155%)',
            background:
              'linear-gradient(to top, rgba(5, 10, 9, 0.66) 0%, rgba(5, 10, 9, 0.46) 46%, rgba(5, 10, 9, 0.18) 76%, rgba(5, 10, 9, 0) 100%)',
            maskImage:
              'linear-gradient(to top, #000 0%, #000 64%, rgba(0, 0, 0, 0.72) 82%, transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to top, #000 0%, #000 64%, rgba(0, 0, 0, 0.72) 82%, transparent 100%)',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            maxWidth: 'var(--app-content-max-width)',
            height: 'var(--app-bottom-nav-height)',
            mx: 'auto',
            pointerEvents: 'auto',
          }}
        >
          <BottomNavigation
            showLabels
            value={getNavValue()}
            onChange={handleNavChange}
            sx={{
              position: 'relative',
              zIndex: 1,
              height: 'var(--app-bottom-nav-height)',
              borderRadius: 999,
              boxShadow:
                'inset 0 1px 0 rgba(244, 255, 249, 0.12), 0 14px 36px rgba(0, 0, 0, 0.34)',
              overflow: 'hidden',
            }}
          >
            <BottomNavigationAction label={t('nav.pairs')} value="pairs" icon={<PairsIcon />} />
            <BottomNavigationAction label={t('nav.calculator')} value="calculator" icon={<CalculatorIcon />} />
            <BottomNavigationAction label={t('nav.profile')} value="profile" icon={<ProfileIcon />} />
            {showAdminTab && isAdmin && (
              <BottomNavigationAction label={t('nav.admin')} value="admin" icon={<AdminIcon />} />
            )}
          </BottomNavigation>
        </Box>
      </Box>
    </Box>
  );
}
